import React from "react";
import Modal from "../../../components/Modal";
import { buildClassroomOptions, resolveClassroomUuids, selectedOptionValues } from "../classroomOptions";
import type { ClassroomGroupOption } from "../classroomOptions";
import { EVENT_TYPES, SCOPES } from "../constants";
import type { CalendarFormState, Classroom, IconComponent, SchoolCalendarEvent, SchoolYear } from "../types";
import { FaCalendarPlus, FaTrash } from "react-icons/fa";
import http from "../../../api/apiClient";
import { translateClassName } from "../../../utils/classroomUtils";
import { addMinutesToDateTime, DURATION_OPTIONS, durationLabel, minutesBetween } from "../utils";

const CalendarPlusIcon = FaCalendarPlus as IconComponent;


const resolveTeacherName = (classroom: Classroom): string => {
  const value = classroom as Classroom & {
    teachers?: Array<{
      firstName?: string;
      lastName?: string;
      firstname?: string;
      lastname?: string;
      name?: string;
      fullName?: string;
    }>;
    teacher?: {
      firstName?: string;
      lastName?: string;
      firstname?: string;
      lastname?: string;
      name?: string;
      fullName?: string;
    } | string;
    teacherFirstName?: string;
    teacherLastName?: string;
    teacherFirstname?: string;
    teacherLastname?: string;
    teacherName?: string;
  };

  const teacherNames = (Array.isArray(value.teachers) ? value.teachers : [])
    .map((teacher) => {
      const composedName = [
        teacher?.firstName ?? teacher?.firstname,
        teacher?.lastName ?? teacher?.lastname,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();

      return composedName || teacher?.fullName || teacher?.name || "";
    })
    .map((name) => name.trim())
    .filter(Boolean);

  if (teacherNames.length > 0) {
    return teacherNames.join(", ");
  }

  if (typeof value.teacher === "string") {
    return value.teacher.trim();
  }

  const firstName =
    value.teacherFirstName ??
    value.teacherFirstname ??
    value.teacher?.firstName ??
    value.teacher?.firstname ??
    "";

  const lastName =
    value.teacherLastName ??
    value.teacherLastname ??
    value.teacher?.lastName ??
    value.teacher?.lastname ??
    "";

  const composedName = [firstName, lastName].filter(Boolean).join(" ").trim();
  if (composedName) return composedName;

  return (
    value.teacherName ??
    value.teacher?.fullName ??
    value.teacher?.name ??
    ""
  ).trim();
};

const buildClassroomOptionLabel = (
  classroom: Classroom,
  classroomLabel: (classroom: Classroom) => string,
): string => {
  const baseLabel = classroomLabel(classroom);
  const teacherName = resolveTeacherName(classroom);

  if (!teacherName) return baseLabel;

  const separator = " — ";
  const parts = baseLabel.split(separator).map((part) => part.trim()).filter(Boolean);

  if (parts.length >= 2) {
    return [parts[0], teacherName, ...parts.slice(1)].join(separator);
  }

  return [baseLabel, teacherName].filter(Boolean).join(separator);
};

interface EventFormModalProps {
  t: any;
  formOpen: boolean;
  setFormOpen: (open: boolean) => void;
  editingEvent: SchoolCalendarEvent | null;
  form: CalendarFormState;
  setForm: React.Dispatch<React.SetStateAction<CalendarFormState>>;
  formErrors: Partial<Record<keyof CalendarFormState, string>>;
  handleSubmit: (event: React.FormEvent) => void;
  saving: boolean;
  scopeLabels: Record<string, string>;
  eventTypeLabels: Record<string, string>;
  sortedSchoolYears: SchoolYear[];
  selectedSchoolYear: SchoolYear | null;
  yearClassrooms: Classroom[];
  entityId: (value?: any) => string | number;
  classroomLabel: (classroom: Classroom) => string;
  schoolYearLabel: (year?: SchoolYear | null) => string;
  canRemove: boolean;
  setDeleteTarget: (event: SchoolCalendarEvent | null) => void;
}

const EventFormModal = ({
  t,
  formOpen,
  setFormOpen,
  editingEvent,
  form,
  setForm,
  formErrors,
  handleSubmit,
  saving,
  scopeLabels,
  eventTypeLabels,
  sortedSchoolYears,
  selectedSchoolYear,
  yearClassrooms,
  entityId,
  classroomLabel,
  schoolYearLabel,
  canRemove,
  setDeleteTarget,
}: EventFormModalProps) => {
  // Duration instead of an end time: "start 12:30, 0,5 hour". "custom" shows the end date and time as before.
  const [duration, setDuration] = React.useState<string>("60");
  React.useEffect(() => {
    if (!formOpen) return;
    if (!editingEvent) {
      setDuration("60");
      return;
    }
    const minutes = minutesBetween(form.startDate, form.startTime, form.endDate, form.endTime);
    setDuration(minutes != null && DURATION_OPTIONS.includes(minutes) ? String(minutes) : "custom");
    // Only when the form opens: afterwards the chosen duration moves the end along with the start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOpen, editingEvent]);
  React.useEffect(() => {
    if (!formOpen || form.allDay || duration === "custom") return;
    const end = addMinutesToDateTime(form.startDate, form.startTime, Number(duration));
    if (end && (end.date !== form.endDate || end.time !== form.endTime)) {
      setForm((current) => ({ ...current, endDate: end.date, endTime: end.time }));
    }
  }, [formOpen, form.allDay, form.startDate, form.startTime, form.endDate, form.endTime, duration, setForm]);
  const useDuration = !form.allDay && duration !== "custom";

  // Location: the school or a classroom from the list, or any other text.
  const [schoolLocation, setSchoolLocation] = React.useState<string>("");
  React.useEffect(() => {
    if (!formOpen || schoolLocation) return undefined;
    let cancelled = false;
    http.get("/admin/school-settings")
      .then(({ data }) => {
        if (cancelled || !data) return;
        const street = [data.street, [data.houseNumber, data.houseNumberAddition].filter(Boolean).join("")].filter(Boolean).join(" ");
        const address = [street, data.city].filter(Boolean).join(", ");
        setSchoolLocation([data.schoolName, address].filter(Boolean).join(", "));
      })
      .catch(() => {
        // Without access to the school settings the school is simply not offered; free text still works.
      });
    return () => {
      cancelled = true;
    };
  }, [formOpen, schoolLocation]);
  const locationOptions = Array.from(new Set([
    schoolLocation,
    ...yearClassrooms.map((classroom) => translateClassName(classroom) || classroom.label || ""),
  ].filter(Boolean)));

  const classroomOptions = buildClassroomOptions(yearClassrooms);
  const classroomGroups = classroomOptions.filter(
    (option): option is ClassroomGroupOption => option.kind === "group",
  );

  return (
  <Modal
    show={formOpen}
    type="manage"
    icon={<CalendarPlusIcon className="text-indigo-500 text-5xl" />}
    title={
      editingEvent
        ? t("schoolCalendar.editTitle", { defaultValue: "Planning-item bewerken" })
        : t("schoolCalendar.createTitle", { defaultValue: "Planning-item toevoegen" })
    }
    onClose={() => setFormOpen(false)}
    hideDefaultActions
    dialogClassName="!w-[min(92vw,44rem)] !max-w-none text-left"
  >
    <form onSubmit={handleSubmit} className="space-y-4 text-left">
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("schoolCalendar.fields.scope", { defaultValue: "Scope" })}
          <select
            value={form.scope}
            onChange={(event) => {
              const nextScope = event.target.value as CalendarFormState["scope"];
              setForm((current) => ({
                ...current,
                scope: nextScope,
                classroomUuids: nextScope === "SCHOOL" ? [] : current.classroomUuids,
              }));
            }}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          >
            {SCOPES.map((scope) => (
              <option key={scope} value={scope}>
                {scopeLabels[scope]}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("schoolCalendar.fields.schoolYear", { defaultValue: "Schooljaar" })}
          <div className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-200">
            {schoolYearLabel(
              sortedSchoolYears.find((year) => String(entityId(year)) === String(form.schoolYearUuid)) ||
                selectedSchoolYear,
            )}
          </div>
        </label>

        {form.scope === "CLASSROOM" && (
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200 md:col-span-2">
            {t("schoolCalendar.fields.classrooms", { defaultValue: "Klassen" })}
            <select
              multiple
              value={selectedOptionValues(classroomGroups, form.classroomUuids)}
              onChange={(event) => {
                const selected = Array.from<HTMLOptionElement>(event.currentTarget.selectedOptions).map(
                  (option) => option.value,
                );
                setForm((current) => ({
                  ...current,
                  classroomUuids: resolveClassroomUuids(selected, current.classroomUuids, classroomGroups),
                }));
              }}
              className="mt-1 min-h-32 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            >
              {classroomOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.kind === "group"
                    ? t(`classrooms.common.levels.${option.key}`, { defaultValue: option.defaultLabel })
                    : buildClassroomOptionLabel(option.classroom, classroomLabel)}
                </option>
              ))}
            </select>
            {formErrors.classroomUuids && (
              <span className="mt-1 block text-xs text-red-600">{formErrors.classroomUuids}</span>
            )}
          </label>
        )}

        <label className="text-sm font-medium text-gray-700 dark:text-gray-200 md:col-span-2">
          {t("schoolCalendar.fields.title", { defaultValue: "Titel" })}
          <input
            type="text"
            value={form.title}
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          />
          {formErrors.title && <span className="mt-1 block text-xs text-red-600">{formErrors.title}</span>}
        </label>

        <label className="text-sm font-medium text-gray-700 dark:text-gray-200 md:col-span-2">
          {t("schoolCalendar.fields.description", { defaultValue: "Omschrijving" })}
          <textarea
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            rows={3}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          />
        </label>

        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("schoolCalendar.fields.eventType", { defaultValue: "Type" })}
          <select
            value={form.eventType}
            onChange={(event) => setForm((current) => ({ ...current, eventType: event.target.value as any }))}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          >
            {EVENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {eventTypeLabels[type]}
              </option>
            ))}
          </select>
          {formErrors.eventType && <span className="mt-1 block text-xs text-red-600">{formErrors.eventType}</span>}
        </label>

        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("schoolCalendar.fields.location", { defaultValue: "Locatie" })}
          <input
            type="text"
            list="school-calendar-locations"
            value={form.location}
            onChange={(event) => setForm((current) => ({ ...current, location: event.target.value }))}
            placeholder={t("schoolCalendar.fields.locationPlaceholder", { defaultValue: "Choose the school or a classroom, or type a location" })}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          />
          <datalist id="school-calendar-locations">
            {locationOptions.map((option) => <option key={option} value={option} />)}
          </datalist>
        </label>

        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("schoolCalendar.fields.startDate", { defaultValue: "Startdatum" })}
          <input
            type="date"
            value={form.startDate}
            onChange={(event) => setForm((current) => ({ ...current, startDate: event.target.value }))}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          />
          {formErrors.startDate && <span className="mt-1 block text-xs text-red-600">{formErrors.startDate}</span>}
        </label>

        {!form.allDay && (
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {t("schoolCalendar.fields.startTime", { defaultValue: "Starttijd" })}
            <input
              type="time"
              step="300"
              value={form.startTime}
              onChange={(event) => setForm((current) => ({ ...current, startTime: event.target.value }))}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            />
            {formErrors.startTime && <span className="mt-1 block text-xs text-red-600">{formErrors.startTime}</span>}
          </label>
        )}

        {!form.allDay && (
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {t("schoolCalendar.fields.duration", { defaultValue: "Duur" })}
            <select
              value={duration}
              onChange={(event) => setDuration(event.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            >
              {DURATION_OPTIONS.map((minutes) => (
                <option key={minutes} value={String(minutes)}>
                  {durationLabel(minutes, t("schoolCalendar.fields.hours", { defaultValue: "uur" }), t("schoolCalendar.fields.minutes", { defaultValue: "min" }))}
                </option>
              ))}
              <option value="custom">{t("schoolCalendar.fields.customEnd", { defaultValue: "Eindtijd zelf kiezen" })}</option>
            </select>
            {useDuration && form.endTime && (
              <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                {form.endDate && form.endDate !== form.startDate
                  ? t("schoolCalendar.fields.untilOtherDay", { date: form.endDate, time: form.endTime, defaultValue: "Tot {{date}} {{time}}" })
                  : t("schoolCalendar.fields.until", { time: form.endTime, defaultValue: "Tot {{time}}" })}
              </span>
            )}
            {useDuration && (formErrors.endDate || formErrors.endTime) && (
              <span className="mt-1 block text-xs text-red-600">{formErrors.endTime || formErrors.endDate}</span>
            )}
          </label>
        )}

        {!useDuration && (
        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("schoolCalendar.fields.endDate", { defaultValue: "Einddatum" })}
          <input
            type="date"
            value={form.endDate}
            onChange={(event) => setForm((current) => ({ ...current, endDate: event.target.value }))}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          />
          {formErrors.endDate && <span className="mt-1 block text-xs text-red-600">{formErrors.endDate}</span>}
        </label>
        )}

        {!form.allDay && !useDuration && (
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {t("schoolCalendar.fields.endTime", { defaultValue: "Eindtijd" })}
            <input
              type="time"
              step="300"
              value={form.endTime}
              onChange={(event) => setForm((current) => ({ ...current, endTime: event.target.value }))}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            />
            {formErrors.endTime && <span className="mt-1 block text-xs text-red-600">{formErrors.endTime}</span>}
          </label>
        )}
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
          <input
            type="checkbox"
            checked={form.allDay}
            onChange={(event) => setForm((current) => ({ ...current, allDay: event.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600"
          />
          {t("schoolCalendar.fields.allDay", { defaultValue: "Hele dag" })}
        </label>

        <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(event) => setForm((current) => ({ ...current, published: event.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600"
          />
          {t("schoolCalendar.fields.published", { defaultValue: "Gepubliceerd" })}
        </label>
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        <div>
          {editingEvent && canRemove && (
            <button
              type="button"
              onClick={() => {
                setFormOpen(false);
                setDeleteTarget(editingEvent);
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:text-red-200 dark:hover:bg-red-900/30"
            >
              <FaTrash />
              {t("common.actions.delete", {
                defaultValue: "Verwijderen",
              })}
            </button>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setFormOpen(false)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            {t("common.cancel", { defaultValue: "Annuleren" })}
          </button>

          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving
              ? t("common.loading", { defaultValue: "Laden..." })
              : t("common.save", { defaultValue: "Opslaan" })}
          </button>
        </div>
      </div>
    </form>
  </Modal>
  );
};

export default EventFormModal;
